import { decodePeriodValue } from '@components'

import { ReactElement, useCallback, useMemo } from 'react'

import { useTrackBalanceEvent } from '@features/balanceLedger'
import { ExpertReportPeriodPicker } from '@features/expertReports'

import { useGlobalFiscalYear } from '../../../../contexts/globalFiscalYearContext'
import { PeriodMode } from '../../../../enums/periodMode'
import type { PeriodValue } from '../../../../types/periodValue'

interface Props {
  encodedPeriod: string
  // Return false when the change was rejected (e.g. fiscal year before the org id is available) so analytics
  // don't record a Period Changed that never applied.
  onChange: (value?: PeriodValue) => boolean
}

export const SuppliersBalancePeriodSelect = ({ encodedPeriod, onChange }: Props): ReactElement => {
  const { setFiscalYear } = useGlobalFiscalYear()
  const trackBalanceEvent = useTrackBalanceEvent()

  const decodedPeriod = useMemo(() => decodePeriodValue(encodedPeriod), [encodedPeriod])

  const handleOpen = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    trackBalanceEvent('Period Picker Opened', { current_period_type: decodedPeriod?.mode })
  }, [decodedPeriod?.mode, trackBalanceEvent])

  const handleChange = useCallback(
    (periodValue: PeriodValue): void => {
      const previousPeriodType = decodedPeriod?.mode

      if (!onChange(periodValue)) {
        return
      }

      // Keep the org-scoped global fiscal year (persisted in localStorage) in sync, matching the side effect
      // `GlobalPeriodSelect` performs today. Only runs after a successful apply so a rejected fiscal-year pick
      // does not leave localStorage ahead of the URL.
      if (periodValue.mode === PeriodMode.FiscalYear && periodValue.value !== undefined) {
        setFiscalYear(periodValue.value)
      }

      /* eslint-disable @typescript-eslint/naming-convention */
      trackBalanceEvent('Period Changed', {
        new_period_type: periodValue.mode,
        previous_period_type: previousPeriodType,
      })
      /* eslint-enable @typescript-eslint/naming-convention */
    },
    [decodedPeriod?.mode, onChange, setFiscalYear, trackBalanceEvent],
  )

  return <ExpertReportPeriodPicker encodedPeriod={encodedPeriod} onChange={handleChange} onOpen={handleOpen} />
}
